/**
 * Parent/child rooms ("spaces"), e.g. an ensuite that is part of its bedroom. Child rooms get no
 * home-screen tile; the parent's page shows "Bedroom · Ensuite" tabs, selected by `?space=`.
 * Links are validated by shared/rooms.ts (same floor, one level), so a child whose parent this
 * user can't see simply stands alone.
 */
import { childSpaceLabel, effectiveParents, parentSpaceLabel, type HomeModel, type Room } from '@home/shared';
import { indexModel, type ModelIndex } from './model.js';
import { EDGE_PX } from './edge-swipe.js';

export interface SpacesIndex {
  /** child room id → parent room id (valid links only). */
  parentOf: Map<string, string>;
  /** parent room id → its child rooms, in display order. */
  children: Map<string, Room[]>;
  /** The home list: floors with child rooms left out. */
  homeFloors: ModelIndex['floors'];
}

const cache = new WeakMap<HomeModel, SpacesIndex>();

export function spacesIndex(model: HomeModel): SpacesIndex {
  const hit = cache.get(model);
  if (hit) return hit;
  const parentOf = effectiveParents(model.rooms);
  const children = new Map<string, Room[]>();
  for (const r of [...model.rooms].sort((a, b) => a.order - b.order)) {
    const p = parentOf.get(r.id);
    if (!p) continue;
    let l = children.get(p);
    if (!l) children.set(p, (l = []));
    l.push(r);
  }
  const homeFloors = indexModel(model)
    .floors.map((f) => ({ floor: f.floor, rooms: f.rooms.filter((r) => !parentOf.has(r.id)) }))
    .filter((f) => f.rooms.length > 0);
  const out: SpacesIndex = { parentOf, children, homeFloors };
  cache.set(model, out);
  return out;
}

export interface Space {
  room: Room;
  /** Short tab label: "Bedroom" for the parent, the child's name minus the parent prefix ("Ensuite"). */
  label: string;
}

/** The parent first, then its children. A room without children is a single space. */
export function spacesOf(model: HomeModel, parent: Room): Space[] {
  const kids = spacesIndex(model).children.get(parent.id) ?? [];
  if (!kids.length) return [{ room: parent, label: parent.name }];
  return [{ room: parent, label: parentSpaceLabel(parent, kids) }, ...kids.map((c) => ({ room: c, label: childSpaceLabel(c, parent) }))];
}

export type RoomRoute =
  | { kind: 'missing' }
  /** Navigate (replace) to the parent with this space selected (null = the parent itself, i.e. drop `?space`). */
  | { kind: 'redirect'; roomId: string; space: string | null }
  | { kind: 'ok'; room: Room; spaces: Space[]; selected: Space };

/**
 * Resolves `/room/:roomId?space=` against the model: a child room redirects to its parent with
 * its tab selected; an unknown `space` is dropped; otherwise the parent and the selected space.
 */
export function roomRoute(model: HomeModel, roomId: string, space: string | null): RoomRoute {
  const room = indexModel(model).rooms.get(roomId);
  if (!room) return { kind: 'missing' };
  const parent = spacesIndex(model).parentOf.get(roomId);
  if (parent) return { kind: 'redirect', roomId: parent, space: roomId };
  const spaces = spacesOf(model, room);
  if (space === null || space === room.id) return { kind: 'ok', room, spaces, selected: spaces[0]! };
  const sel = spaces.find((s) => s.room.id === space);
  if (!sel) return { kind: 'redirect', roomId: room.id, space: null };
  return { kind: 'ok', room, spaces, selected: sel };
}

/** URL for a room (or a child space, which lives on its parent's page). */
export function roomHref(home: string, model: HomeModel | undefined, roomId: string): string {
  const parent = model ? spacesIndex(model).parentOf.get(roomId) : undefined;
  return parent ? `/h/${home}/room/${parent}?space=${encodeURIComponent(roomId)}` : `/h/${home}/room/${roomId}`;
}

// ---------------------------------------------------------------- tab swipe

/** A tab swipe never starts this close to a screen edge: that strip belongs to the edge-swipe back/forward. */
export const TAB_SWIPE_EDGE_PX = EDGE_PX * 2;
/** Horizontal travel that switches tabs on release. */
export const TAB_SWIPE_COMMIT_PX = 56;

export function tabSwipeCanStart(x: number, width: number): boolean {
  return x >= TAB_SWIPE_EDGE_PX && x <= width - TAB_SWIPE_EDGE_PX;
}

/**
 * Tab step for a finished drag: +1 = next tab (finger moved left), -1 = previous, 0 = none.
 * Needs mostly-horizontal travel so vertical scrolling never switches tabs.
 */
export function tabSwipeStep(dx: number, dy: number): -1 | 0 | 1 {
  if (Math.abs(dx) < TAB_SWIPE_COMMIT_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}
